import { zodResolver } from '@hookform/resolvers/zod';
import { ApiError, createOrganization } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import {
  type CreateOrganizationRequest,
  CreateOrganizationRequestSchema,
  DEFAULT_COUNTRY_CODE,
  DEFAULT_TIMEZONE,
} from '@jq4race/shared';
import { Button, buttonVariants, Card, CardContent, Input, Label } from '@jq4race/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, TriangleAlert } from 'lucide-react';
import { forwardRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { enumLabel } from '../../../../shared/i18n/enum-label';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { COUNTRY_OPTIONS, NICHE_OPTIONS, TIMEZONE_OPTIONS } from '../constants';

export function NewOrganizationPage() {
  const { t } = useTranslation('organizations');
  const navigate = useNavigate();

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('newOrg.eyebrow')}
        title={t('newOrg.title')}
        description={t('newOrg.description')}
        actions={
          <Link
            to="/super-admin/organizations"
            className={buttonVariants({ size: 'sm', variant: 'ghost' })}
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
            {t('newOrg.backToList')}
          </Link>
        }
      />

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          <OrgForm
            onCreated={() => {
              void navigate({ to: '/super-admin/organizations' });
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function OrgForm({ onCreated }: { onCreated: () => void }) {
  const { t } = useTranslation('organizations');
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<CreateOrganizationRequest>({
    resolver: zodResolver(CreateOrganizationRequestSchema),
    defaultValues: {
      name: '',
      legalName: '',
      country: DEFAULT_COUNTRY_CODE,
      timezone: DEFAULT_TIMEZONE,
      niche: 'restaurant',
    },
  });

  const [serverError, setServerError] = useState<string | null>(null);

  async function onSubmit(values: CreateOrganizationRequest) {
    setServerError(null);
    try {
      await createOrganization(values);
      onCreated();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setError('name', {
          type: 'server',
          message: translateApiError(e, t, { fallback: t('newOrg.nameTaken') }),
        });
        return;
      }
      setServerError(translateApiError(e, t, { fallback: t('newOrg.createError') }));
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {serverError && <FormBanner message={serverError} />}

      <div className="grid gap-6 md:grid-cols-2">
        <Field
          label={t('newOrg.fields.legalName.label')}
          htmlFor="legalName"
          error={errors.legalName?.message}
        >
          <Input
            id="legalName"
            placeholder={t('newOrg.fields.legalName.placeholder')}
            autoComplete="organization"
            disabled={isSubmitting}
            {...register('legalName')}
          />
        </Field>

        <Field
          label={t('newOrg.fields.displayName.label')}
          htmlFor="name"
          error={errors.name?.message}
        >
          <Input
            id="name"
            placeholder={t('newOrg.fields.displayName.placeholder')}
            disabled={isSubmitting}
            {...register('name')}
            aria-describedby="name-help"
          />
          <p id="name-help" className="mt-1 text-[12px] text-jq-graphite">
            {t('newOrg.fields.displayName.help')}
          </p>
        </Field>

        <Field
          label={t('newOrg.fields.country.label')}
          htmlFor="country"
          error={errors.country?.message}
        >
          <SelectInput id="country" disabled={isSubmitting} {...register('country')}>
            {COUNTRY_OPTIONS.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label} ({c.code})
              </option>
            ))}
          </SelectInput>
        </Field>

        <Field
          label={t('newOrg.fields.timezone.label')}
          htmlFor="timezone"
          error={errors.timezone?.message}
        >
          <SelectInput id="timezone" disabled={isSubmitting} {...register('timezone')}>
            {TIMEZONE_OPTIONS.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </SelectInput>
        </Field>

        <Field
          label={t('newOrg.fields.niche.label')}
          htmlFor="niche"
          error={errors.niche?.message}
          className="md:col-span-2"
        >
          <SelectInput id="niche" disabled={isSubmitting} {...register('niche')}>
            {NICHE_OPTIONS.map((n) => (
              <option key={n.value} value={n.value}>
                {enumLabel(t, 'niche', n.value)}
              </option>
            ))}
          </SelectInput>
        </Field>
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-jq-sand/70 pt-6">
        <Link
          to="/super-admin/organizations"
          className={`${buttonVariants({ size: 'sm', variant: 'ghost' })} ${
            isSubmitting ? 'pointer-events-none opacity-50' : ''
          }`}
        >
          {t('newOrg.cancel')}
        </Link>
        <Button type="submit" size="sm" disabled={isSubmitting}>
          {isSubmitting ? t('newOrg.submitting') : t('newOrg.submit')}
          <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
        </Button>
      </div>
    </form>
  );
}

// ---------- Building blocks ----------

function Field({
  label,
  htmlFor,
  error,
  className,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className ?? ''}>
      <Label htmlFor={htmlFor} className="mb-1.5 block text-[13px] font-medium text-jq-charcoal">
        {label}
      </Label>
      {children}
      {error && (
        <p role="alert" className="mt-1.5 text-[12px] text-jq-danger">
          {error}
        </p>
      )}
    </div>
  );
}

const SelectInput = forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  function SelectInput({ className, ...rest }, ref) {
    return (
      <select
        ref={ref}
        {...rest}
        className={`flex h-10 w-full rounded-sm border border-jq-sand bg-jq-surface-0 px-3 text-[14px] text-jq-charcoal shadow-sm transition focus:border-jq-charcoal focus:outline-none focus:ring-1 focus:ring-jq-charcoal disabled:cursor-not-allowed disabled:opacity-50 ${
          className ?? ''
        }`}
      />
    );
  },
);

function FormBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
    </div>
  );
}
